import React, { useCallback } from "react";

import Form from "antd/es/form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import cn from "classnames";
import { useIntl } from "react-intl";

import Checkbox from "@app/component/input/Checkbox";
import NotificationSettingValue from "@app/types/notifications/NotificationSetting";

import styles from "../../NotificationsCommon.module.scss";

import DealClosing from "./DealClosing";
import getRecordOwnershipDealLevelSettingsState from "./utils/getRecordOwnershipDealLevelSettingsState";

interface Props {
  form: FormInstance<NotificationSettingValue>;
  level: "owned" | "shared";
}

const RecordUpdateFields: React.FC<Props> = ({ form, level }) => {
  const intl = useIntl();

  const enabledNamePath: string[] = ["recordUpdates", level, "enabled"];
  const dealStagesNamePath: string[] = ["recordUpdates", level, "dealStages", "enabled"];

  const handleChangeSetting = useCallback(
    (name: "outOfCadence" | "overdueActivities" | "rottedDeals") => (enabled: boolean) => {
      form.setFieldsValue({
        recordUpdates: { [level]: { [name]: enabled } },
      });
    },
    [form, level]
  );

  const handleToggleChangeDealStages = useCallback(
    (name: "closedLost" | "closedWon" | "enabled" | "openStage") => (enabled: boolean) => {
      form.setFieldsValue({
        recordUpdates: {
          [level]: {
            dealStages:
              name === "enabled"
                ? getRecordOwnershipDealLevelSettingsState(enabled)
                : { [name]: enabled },
          },
        },
      });
    },
    [form, level]
  );

  return (
    <div>
      <Form.Item
        className={styles.formItem}
        shouldUpdate={(prev, next) =>
          prev.recordUpdates[level].enabled !== next.recordUpdates[level].enabled ||
          prev.recordUpdates[level].outOfCadence !== next.recordUpdates[level].outOfCadence
        }
      >
        {({ getFieldValue }) => (
          <Checkbox
            checked={getFieldValue(["recordUpdates", level, "outOfCadence"])}
            className={cn(styles.secondLevelSetting, styles.secondaryCheckboxText)}
            disabled={!getFieldValue(enabledNamePath)}
            onChange={handleChangeSetting("outOfCadence")}
          >
            {intl.formatMessage({
              id: "settings.notifications.recordUpdates.level.outOfCadence",
              defaultMessage: "Include records that are past due",
              description:
                "Settings notifications Record Updates - records you own - out if cadence",
            })}
          </Checkbox>
        )}
      </Form.Item>
      <Form.Item
        className={styles.formItem}
        shouldUpdate={(prev, next) =>
          prev.recordUpdates[level].enabled !== next.recordUpdates[level].enabled ||
          prev.recordUpdates[level].overdueActivities !==
            next.recordUpdates[level].overdueActivities
        }
      >
        {({ getFieldValue }) => (
          <Checkbox
            checked={getFieldValue(["recordUpdates", level, "overdueActivities"])}
            className={cn(styles.secondLevelSetting, styles.secondaryCheckboxText)}
            disabled={!getFieldValue(enabledNamePath)}
            onChange={handleChangeSetting("overdueActivities")}
          >
            {intl.formatMessage({
              id: "settings.notifications.recordUpdates.level.overdueActivities",
              defaultMessage: "Include activities that have become overdue",
              description:
                "Settings notifications Record Updates - records you own - overdue Activities",
            })}
          </Checkbox>
        )}
      </Form.Item>
      <Form.Item
        className={styles.formItem}
        shouldUpdate={(prev, next) =>
          prev.recordUpdates[level].enabled !== next.recordUpdates[level].enabled ||
          prev.recordUpdates[level].rottedDeals !== next.recordUpdates[level].rottedDeals
        }
      >
        {({ getFieldValue }) => (
          <Checkbox
            checked={getFieldValue(["recordUpdates", level, "rottedDeals"])}
            className={cn(styles.secondLevelSetting, styles.secondaryCheckboxText)}
            disabled={!getFieldValue(enabledNamePath)}
            onChange={handleChangeSetting("rottedDeals")}
          >
            {intl.formatMessage({
              id: "settings.notifications.recordUpdates.level.rottedDeals",
              defaultMessage: "Include deals that have started rotting",
              description: "Settings notifications Record Updates - records you own - rotted Deals",
            })}
          </Checkbox>
        )}
      </Form.Item>
      <Form.Item
        className={styles.formItem}
        shouldUpdate={(prev, next) =>
          prev.recordUpdates[level].enabled !== next.recordUpdates[level].enabled ||
          prev.recordUpdates[level].dealStages.enabled !==
            next.recordUpdates[level].dealStages.enabled
        }
      >
        {({ getFieldValue }) => (
          <Checkbox
            checked={getFieldValue(dealStagesNamePath)}
            className={cn(styles.secondLevelSetting, styles.secondaryCheckboxText)}
            disabled={!getFieldValue(enabledNamePath)}
            onChange={handleToggleChangeDealStages("enabled")}
          >
            {intl.formatMessage({
              id: "settings.notifications.recordUpdates.level.dealStages",
              defaultMessage: "Include deals where stage is updated",
              description: "Settings notifications Record Updates - records you own - deal Stages",
            })}
          </Checkbox>
        )}
      </Form.Item>
      <Form.Item
        className={styles.formItem}
        shouldUpdate={(prev, next) =>
          prev.recordUpdates[level].dealStages.enabled !==
            next.recordUpdates[level].dealStages.enabled ||
          prev.recordUpdates[level].dealStages.closedWon !==
            next.recordUpdates[level].dealStages.closedWon
        }
      >
        {({ getFieldValue }) => (
          <Checkbox
            checked={getFieldValue(["recordUpdates", level, "dealStages", "closedWon"])}
            className={cn(styles.thirdLevelSetting, styles.secondaryCheckboxText)}
            disabled={!getFieldValue(dealStagesNamePath)}
            onChange={handleToggleChangeDealStages("closedWon")}
          >
            {intl.formatMessage({
              id: "settings.notifications.recordUpdates.level.dealStages.closedWon",
              defaultMessage: "Deals marked as Closed Won",
              description:
                "Settings notifications Record Updates - records you own - deal Stages - closed Won",
            })}
          </Checkbox>
        )}
      </Form.Item>
      <Form.Item
        className={styles.formItem}
        shouldUpdate={(prev, next) =>
          prev.recordUpdates[level].dealStages.enabled !==
            next.recordUpdates[level].dealStages.enabled ||
          prev.recordUpdates[level].dealStages.closedLost !==
            next.recordUpdates[level].dealStages.closedLost
        }
      >
        {({ getFieldValue }) => (
          <Checkbox
            checked={getFieldValue(["recordUpdates", level, "dealStages", "closedLost"])}
            className={cn(styles.thirdLevelSetting, styles.secondaryCheckboxText)}
            disabled={!getFieldValue(dealStagesNamePath)}
            onChange={handleToggleChangeDealStages("closedLost")}
          >
            {intl.formatMessage({
              id: "settings.notifications.recordUpdates.level.dealStages.closedLost",
              defaultMessage: "Deals marked as Closed Lost",
              description:
                "Settings notifications Record Updates - records you own - deal Stages - closed Lost",
            })}
          </Checkbox>
        )}
      </Form.Item>
      <Form.Item
        className={styles.formItem}
        shouldUpdate={(prev, next) =>
          prev.recordUpdates[level].dealStages.enabled !==
            next.recordUpdates[level].dealStages.enabled ||
          prev.recordUpdates[level].dealStages.openStage !==
            next.recordUpdates[level].dealStages.openStage
        }
      >
        {({ getFieldValue }) => (
          <Checkbox
            checked={getFieldValue(["recordUpdates", level, "dealStages", "openStage"])}
            className={cn(styles.thirdLevelSetting, styles.secondaryCheckboxText)}
            disabled={!getFieldValue(dealStagesNamePath)}
            onChange={handleToggleChangeDealStages("openStage")}
          >
            {intl.formatMessage({
              id: "settings.notifications.recordUpdates.level.dealStages.openStage",
              defaultMessage: "Deals changed to any open stage",
              description:
                "Settings notifications Record Updates - records you own - deal Stages - open Stage",
            })}
          </Checkbox>
        )}
      </Form.Item>
      <DealClosing enabledNamePath={enabledNamePath} form={form} level={level} />
    </div>
  );
};

export default RecordUpdateFields;
